import React, { type ReactNode } from "react";
import {
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconExternalLink,
  IconLayoutGrid,
  IconDeviceDesktopAnalytics,
  IconLayoutSidebarRightExpand,
  IconCheck,
  IconArrowLeft,
  IconBrandGithub,
} from "@tabler/icons-react";
import { useParams, Link } from "react-router-dom";

// Import JSON data
import projectsData from "../data/projects.json";
import { ArchitectureStackDiagram } from "../components/ArchitectureStackDiagram";
import { DeploymentSwimlaneDiagram } from "../components/DeploymentSwimlaneDiagram";

interface Technology {
  name: string;
  description: string;
}

interface TechnologyGroup {
  category: string;
  technologies: Technology[];
}

interface ProjectTag {
  name: string;
}

interface ProjectEntry {
  id: string;
  title: string;
  slug: string;
  description: string;
  content?: string;
  status: string;
  startDate: string;
  endDate?: string | null;
  repositoryUrl?: string;
  liveUrl?: string;
  technologyGroups?: TechnologyGroup[];
  tags?: ProjectTag[];
}

interface SectionProps {
  title: string;
  right?: ReactNode;
  children: ReactNode;
}

// Helper: pill component for tech stack tags
const TechPill = ({ label }: { label: string }) => (
  <Badge
    variant="filled"
    size="sm"
    style={{
      backgroundColor: "#B97F24",
      color: "white",
    }}
  >
    {label}
  </Badge>
);

// Reusable section wrapper
const Section = ({ title, right, children }: SectionProps) => (
  <Paper
    p="xl"
    className="hover-lift"
    style={{
      backgroundColor: "var(--card-background)",
      border: "1px solid rgba(139, 66, 255, 0.15)",
      borderRadius: "16px",
    }}
  >
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Group align="center">
          <Title order={2} className="text-primary-dark" fw={600}>
            {title}
          </Title>
        </Group>
        {right}
      </Group>
      {children}
    </Stack>
  </Paper>
);

// Function to format markdown-style content into React components
function formatContent(content: string) {
  const sections = content.split("\n\n");

  return sections
    .map((section, index) => {
      // Handle section headers (bold text followed by colon)
      if (section.includes("**") && section.includes(":")) {
        const headerMatch = section.match(/\*\*(.*?)\*\*:/);
        if (headerMatch) {
          const header = headerMatch[1];
          const listContent = section
            .substring(section.indexOf(":") + 1)
            .trim();

          return (
            <Stack key={index} gap="sm">
              <Title order={4} className="text-primary-dark" fw={600} mt="md">
                {header}
              </Title>
              {listContent && (
                <List spacing="xs" className="text-secondary-dark">
                  {listContent
                    .split("\n")
                    .filter((line) => line.trim().startsWith("-"))
                    .map((item, itemIndex) => {
                      const cleanItem = item
                        .replace(/^-\s*/, "")
                        .replace(/\*\*(.*?)\*\*/g, "$1");
                      const [title, ...description] = cleanItem.split(":");

                      return (
                        <List.Item
                          key={itemIndex}
                          icon={
                            <IconCheck size={16} color="var(--purple-400)" />
                          }
                        >
                          <Text
                            component="span"
                            fw={500}
                            className="text-primary-dark"
                          >
                            {title}
                          </Text>
                          {description.length > 0 && (
                            <Text
                              component="span"
                              className="text-secondary-dark"
                            >
                              : {description.join(":").trim()}
                            </Text>
                          )}
                        </List.Item>
                      );
                    })}
                </List>
              )}
            </Stack>
          );
        }
      }

      // Handle regular paragraphs
      const cleanSection = section.replace(/\*\*(.*?)\*\*/g, "$1");
      if (cleanSection.trim()) {
        return (
          <Text
            key={index}
            className="text-secondary-dark"
            style={{ lineHeight: 1.6 }}
            size="md"
          >
            {cleanSection}
          </Text>
        );
      }

      return null;
    })
    .filter(Boolean);
}

// Helper function to get status color
function getStatusColor(status: string) {
  switch (status.toUpperCase()) {
    case "ACTIVE":
      return { bg: "rgba(34, 197, 94, 0.2)", border: "rgba(34, 197, 94, 0.3)" };
    case "COMPLETED":
      return {
        bg: "rgba(59, 130, 246, 0.2)",
        border: "rgba(59, 130, 246, 0.3)",
      };
    case "ON_HOLD":
    case "PAUSED":
      return {
        bg: "rgba(245, 158, 11, 0.2)",
        border: "rgba(245, 158, 11, 0.3)",
      };
    case "ARCHIVED":
      return {
        bg: "rgba(107, 114, 128, 0.2)",
        border: "rgba(107, 114, 128, 0.3)",
      };
    default:
      return {
        bg: "rgba(139, 66, 255, 0.2)",
        border: "rgba(139, 66, 255, 0.3)",
      };
  }
}

// Helper function to get status label
function getStatusLabel(status: string) {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "COMPLETED":
      return "Completed";
    case "ON_HOLD":
      return "On Hold";
    case "ARCHIVED":
      return "Archived";
    default:
      return status;
  }
}

// Helper function to get project type
function getProjectType(project: ProjectEntry) {
  switch (project.id) {
    case "aoe-ashes-of-empire":
      return "Tabletop";
    default:
      return "Full-Stack";
  }
}

// Helper function to get project role
function getProjectRole(project: ProjectEntry) {
  switch (project.id) {
    case "aoe-ashes-of-empire":
      return "Designer & Developer";
    default:
      return "Full-Stack Developer";
  }
}

// Helper function to calculate project metrics
function getProjectMetrics(project: ProjectEntry) {
  const techCount = (project.technologyGroups ?? []).reduce(
    (acc, group) => acc + group.technologies.length,
    0
  );
  const startYear = project.startDate
    ? new Date(project.startDate + "T00:00:00").getFullYear()
    : new Date().getFullYear();
  const endYear = project.endDate
    ? new Date(project.endDate + "T00:00:00").getFullYear()
    : new Date().getFullYear();
  const duration = project.endDate
    ? `${startYear} - ${endYear}`
    : `${startYear} - Present`;

  return { techCount, duration };
}

export function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  // Find project by slug
  const projects = projectsData.projects as ProjectEntry[];
  const project = projects.find((p) => p.slug === slug);

  if (!project)
    return (
      <Box style={{ background: "var(--deep-background)", minHeight: "100vh" }}>
        <Container size="lg" py="xl">
          <Text size="xl" className="text-primary-dark" ta="center">
            Project not found
          </Text>
        </Container>
      </Box>
    );

  const statusColors = getStatusColor(project.status);
  const metrics = getProjectMetrics(project);
  const technologyGroups = project.technologyGroups ?? [];
  const tags = project.tags ?? [];

  return (
    <Box style={{ background: "var(--deep-background)", minHeight: "100vh" }}>
      <Container size="lg" py="xl">
        <Stack gap="xl">
          {/* Back Button */}
          <Group>
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              component={Link}
              to="/projects"
              className="hover-lift"
              style={{
                color: "var(--purple-400)",
                backgroundColor: "rgba(139, 66, 255, 0.1)",
                border: "1px solid rgba(139, 66, 255, 0.2)",
              }}
            >
              Back to Projects
            </Button>
          </Group>

          {/* Header / Title Bar */}
          <Paper
            p="xl"
            className="hover-lift"
            style={{
              backgroundColor: "var(--card-background)",
              border: "1px solid rgba(139, 66, 255, 0.15)",
              borderRadius: "16px",
            }}
          >
            <Grid>
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <Group justify="space-between" align="flex-start">
                    <Stack gap="xs">
                      <Group>
                        <Badge
                          size="lg"
                          variant="light"
                          style={{
                            background: statusColors.bg,
                            color: "var(--text-primary)",
                            border: `1px solid ${statusColors.border}`,
                          }}
                        >
                          {getStatusLabel(project.status)}
                        </Badge>
                        <Text className="text-secondary-dark" size="lg">
                          {metrics.duration}
                        </Text>
                      </Group>
                      <Title
                        order={1}
                        className="text-gradient"
                        style={{ fontSize: "2.5rem", fontWeight: 700 }}
                      >
                        {project.title}
                      </Title>
                      <Text size="lg" className="text-secondary-dark" lh={1.6}>
                        {project.description}
                      </Text>

                      <Group mt="lg">
                        {project.repositoryUrl && (
                          <Button
                            leftSection={<IconBrandGithub size={16} />}
                            variant="outline"
                            component="a"
                            href={project.repositoryUrl}
                            target="_blank"
                            className="hover-lift"
                            style={{
                              borderColor: "var(--purple-500)",
                              color: "var(--purple-400)",
                              backgroundColor: "rgba(139, 66, 255, 0.1)",
                            }}
                          >
                            View Code
                          </Button>
                        )}
                        {project.liveUrl && (
                          <Button
                            leftSection={<IconExternalLink size={16} />}
                            component="a"
                            href={project.liveUrl}
                            target="_blank"
                            className="btn-gradient hover-lift"
                            style={{
                              background: "var(--primary-gradient)",
                              color: "white",
                              border: "none",
                            }}
                          >
                            Live Demo
                          </Button>
                        )}
                      </Group>
                    </Stack>
                  </Group>
                </Stack>
              </Grid.Col>

              {/* Quick Stats Card */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Paper
                  p="md"
                  style={{
                    backgroundColor: "var(--surface-background)",
                    border: "1px solid rgba(139, 66, 255, 0.2)",
                    borderRadius: "12px",
                  }}
                >
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text className="text-secondary-dark" size="sm">
                        Technologies
                      </Text>
                      <Text className="text-primary-dark" fw={600}>
                        {metrics.techCount}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text className="text-secondary-dark" size="sm">
                        Status
                      </Text>
                      <Text className="text-primary-dark" fw={600}>
                        {getStatusLabel(project.status)}
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Text className="text-secondary-dark" size="sm">
                        Type
                      </Text>
                      <Text className="text-primary-dark" fw={600}>
                        {getProjectType(project)}
                      </Text>
                    </Group>
                    <Group gap="xs" mt="xs">
                      <Badge size="xs" variant="light" color="green">
                        Modern
                      </Badge>
                      <Badge size="xs" variant="light" color="blue">
                        Scalable
                      </Badge>
                      <Badge size="xs" variant="light" color="purple">
                        Production
                      </Badge>
                    </Group>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Main Grid */}
          <Grid>
            <Grid.Col span={{ base: 12, lg: 8 }}>
              <Stack gap="xl">
                {/* About / Overview */}
                {project.content && (
                  <Section title="Project Overview">
                    <Stack gap="lg">{formatContent(project.content)}</Stack>
                  </Section>
                )}

                {project.id === "dachshund-app" && (
                  <Section title="System Visualizations">
                    <Stack gap="xl">
                      <ArchitectureStackDiagram />
                      <DeploymentSwimlaneDiagram />
                    </Stack>
                  </Section>
                )}

                {/* Screenshots section */}
                <Section title="Screenshots">
                  {project.id === "aoe-ashes-of-empire" ? (
                    <>
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                        <Box
                          style={{
                            height: "300px",
                            backgroundColor: "var(--surface-background)",
                            border: "1px solid rgba(139, 66, 255, 0.2)",
                            borderRadius: "12px",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <img
                            src="/assets/images/design/global.png"
                            alt="Global Map Overview - Strategic view of post-WWI Europe showing faction territories and influence zones"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                          <Box
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background:
                                "linear-gradient(transparent, rgba(0,0,0,0.8))",
                              padding: "1rem",
                              color: "white",
                            }}
                          >
                            <Text fw={600} size="sm">
                              Global Map Overview
                            </Text>
                            <Text size="xs" opacity={0.9}>
                              Strategic view of post-WWI Europe
                            </Text>
                          </Box>
                        </Box>

                        <Box
                          style={{
                            height: "300px",
                            backgroundColor: "var(--surface-background)",
                            border: "1px solid rgba(139, 66, 255, 0.2)",
                            borderRadius: "12px",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <img
                            src="/assets/images/design/interventionists.png"
                            alt="Interventionist Faction Cards - Specialized card deck showing political and military operations"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                          <Box
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background:
                                "linear-gradient(transparent, rgba(0,0,0,0.8))",
                              padding: "1rem",
                              color: "white",
                            }}
                          >
                            <Text fw={600} size="sm">
                              Interventionist Cards
                            </Text>
                            <Text size="xs" opacity={0.9}>
                              Faction-specific operations and events
                            </Text>
                          </Box>
                        </Box>
                      </SimpleGrid>

                      <SimpleGrid
                        cols={{ base: 1, md: 2 }}
                        spacing="md"
                        mt="md"
                      >
                        <Box
                          style={{
                            height: "300px",
                            backgroundColor: "var(--surface-background)",
                            border: "1px solid rgba(139, 66, 255, 0.2)",
                            borderRadius: "12px",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <img
                            src="/assets/images/design/unrest.png"
                            alt="Unrest and Political Mechanics - Game board showing loyalty shifts and political instability"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                          <Box
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background:
                                "linear-gradient(transparent, rgba(0,0,0,0.8))",
                              padding: "1rem",
                              color: "white",
                            }}
                          >
                            <Text fw={600} size="sm">
                              Political Unrest System
                            </Text>
                            <Text size="xs" opacity={0.9}>
                              Loyalty shifts and societal collapse
                            </Text>
                          </Box>
                        </Box>

                        <Box
                          style={{
                            height: "300px",
                            backgroundColor: "var(--surface-background)",
                            border: "1px solid rgba(139, 66, 255, 0.2)",
                            borderRadius: "12px",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          <img
                            src="/assets/images/design/cover1.png"
                            alt="Game Components Overview - Complete game setup showing cards, blocks, and game board"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                          <Box
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              background:
                                "linear-gradient(transparent, rgba(0,0,0,0.8))",
                              padding: "1rem",
                              color: "white",
                            }}
                          >
                            <Text fw={600} size="sm">
                              Game Components
                            </Text>
                            <Text size="xs" opacity={0.9}>
                              Complete game setup and materials
                            </Text>
                          </Box>
                        </Box>
                      </SimpleGrid>

                      <Text
                        className="text-secondary-dark"
                        size="sm"
                        ta="center"
                        mt="lg"
                        style={{ fontStyle: "italic" }}
                      >
                        Screenshots from active development and playtesting
                        sessions. Game design emphasizes historical authenticity
                        with engaging strategic gameplay.
                      </Text>
                    </>
                  ) : (
                    <>
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                        <Box
                          style={{
                            height: "200px",
                            backgroundColor: "var(--surface-background)",
                            border: "2px dashed rgba(139, 66, 255, 0.3)",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Text className="text-muted-dark">
                            Homepage Screenshot
                          </Text>
                        </Box>
                        <Box
                          style={{
                            height: "200px",
                            backgroundColor: "var(--surface-background)",
                            border: "2px dashed rgba(139, 66, 255, 0.3)",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Text className="text-muted-dark">
                            Dashboard View
                          </Text>
                        </Box>
                        <Box
                          style={{
                            height: "200px",
                            backgroundColor: "var(--surface-background)",
                            border: "2px dashed rgba(139, 66, 255, 0.3)",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Text className="text-muted-dark">Detail View</Text>
                        </Box>
                        <Box
                          style={{
                            height: "200px",
                            backgroundColor: "var(--surface-background)",
                            border: "2px dashed rgba(139, 66, 255, 0.3)",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Text className="text-muted-dark">Mobile View</Text>
                        </Box>
                      </SimpleGrid>
                      <Text
                        className="text-muted-dark"
                        size="sm"
                        ta="center"
                        mt="md"
                      >
                        Screenshots will be added as the project develops.
                        Consistent aspect ratios for clean presentation.
                      </Text>
                    </>
                  )}
                </Section>

                {/* Architecture / Data Flow - Only show for software projects */}
                {project.id !== "aoe-ashes-of-empire" && (
                  <Section
                    title="Architecture"
                    right={
                      <Badge variant="light" color="purple">
                        Modern Stack
                      </Badge>
                    }
                  >
                    <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
                      <Paper
                        p="md"
                        style={{
                          backgroundColor: "var(--surface-background)",
                          border: "1px solid rgba(139, 66, 255, 0.2)",
                          borderRadius: "12px",
                        }}
                      >
                        <Stack gap="sm">
                          <ThemeIcon size="lg" variant="light" color="blue">
                            <IconLayoutGrid size={20} />
                          </ThemeIcon>
                          <Text className="text-primary-dark" fw={600}>
                            Frontend
                          </Text>
                          <List size="sm" className="text-secondary-dark">
                            <List.Item>React with TypeScript</List.Item>
                            <List.Item>Component-based architecture</List.Item>
                            <List.Item>Modern routing & state</List.Item>
                            <List.Item>Responsive design</List.Item>
                          </List>
                        </Stack>
                      </Paper>

                      <Paper
                        p="md"
                        style={{
                          backgroundColor: "var(--surface-background)",
                          border: "1px solid rgba(139, 66, 255, 0.2)",
                          borderRadius: "12px",
                        }}
                      >
                        <Stack gap="sm">
                          <ThemeIcon size="lg" variant="light" color="green">
                            <IconDeviceDesktopAnalytics size={20} />
                          </ThemeIcon>
                          <Text className="text-primary-dark" fw={600}>
                            API Layer
                          </Text>
                          <List size="sm" className="text-secondary-dark">
                            <List.Item>GraphQL schema design</List.Item>
                            <List.Item>Type-safe resolvers</List.Item>
                            <List.Item>Efficient data fetching</List.Item>
                            <List.Item>Real-time capabilities</List.Item>
                          </List>
                        </Stack>
                      </Paper>

                      <Paper
                        p="md"
                        style={{
                          backgroundColor: "var(--surface-background)",
                          border: "1px solid rgba(139, 66, 255, 0.2)",
                          borderRadius: "12px",
                        }}
                      >
                        <Stack gap="sm">
                          <ThemeIcon size="lg" variant="light" color="orange">
                            <IconLayoutSidebarRightExpand size={20} />
                          </ThemeIcon>
                          <Text className="text-primary-dark" fw={600}>
                            Database
                          </Text>
                          <List size="sm" className="text-secondary-dark">
                            <List.Item>PostgreSQL with Prisma</List.Item>
                            <List.Item>Schema migrations</List.Item>
                            <List.Item>Optimized queries</List.Item>
                            <List.Item>Data integrity</List.Item>
                          </List>
                        </Stack>
                      </Paper>
                    </SimpleGrid>
                  </Section>
                )}
              </Stack>
            </Grid.Col>

            {/* Right rail */}
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <Stack gap="md">
                {/* Tech Stack */}
                {technologyGroups.length > 0 && (
                  <Paper
                    p="md"
                    className="hover-lift"
                    style={{
                      backgroundColor: "var(--card-background)",
                      border: "1px solid rgba(139, 66, 255, 0.15)",
                      borderRadius: "16px",
                    }}
                  >
                    <Stack gap="md">
                      <Title
                        order={3}
                        className="text-primary-dark"
                        fw={600}
                        size="lg"
                      >
                        Tech Stack
                      </Title>

                      <Stack gap="sm">
                        {technologyGroups.map((group) => (
                          <Box key={group.category}>
                            <Text
                              fw={600}
                              className="text-primary-dark"
                              size="xs"
                              mb={6}
                              opacity={0.8}
                            >
                              {group.category.toUpperCase()}
                            </Text>
                            <Group gap={6}>
                              {group.technologies.map((tech) => (
                                <TechPill key={tech.name} label={tech.name} />
                              ))}
                            </Group>
                          </Box>
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                )}

                {/* Project Info */}
                <Paper
                  p="md"
                  className="hover-lift"
                  style={{
                    backgroundColor: "var(--card-background)",
                    border: "1px solid rgba(139, 66, 255, 0.15)",
                    borderRadius: "16px",
                  }}
                >
                  <Stack gap="md">
                    <Title
                      order={3}
                      className="text-primary-dark"
                      fw={600}
                      size="lg"
                    >
                      Project Info
                    </Title>

                    <Table
                      style={{
                        backgroundColor: "transparent",
                      }}
                    >
                      <Table.Tbody>
                        <Table.Tr>
                          <Table.Td className="text-secondary-dark" fw={500}>
                            Role
                          </Table.Td>
                          <Table.Td className="text-primary-dark">
                            {getProjectRole(project)}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                          <Table.Td className="text-secondary-dark" fw={500}>
                            Duration
                          </Table.Td>
                          <Table.Td className="text-primary-dark">
                            {metrics.duration}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                          <Table.Td className="text-secondary-dark" fw={500}>
                            Status
                          </Table.Td>
                          <Table.Td className="text-primary-dark">
                            {getStatusLabel(project.status)}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                          <Table.Td className="text-secondary-dark" fw={500}>
                            Type
                          </Table.Td>
                          <Table.Td className="text-primary-dark">
                            {getProjectType(project)}
                          </Table.Td>
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>

                    <Divider
                      style={{ borderColor: "rgba(139, 66, 255, 0.2)" }}
                    />

                    <Stack gap="xs">
                      {project.liveUrl && (
                        <Button
                          leftSection={<IconExternalLink size={16} />}
                          component="a"
                          href={project.liveUrl}
                          target="_blank"
                          className="btn-gradient hover-lift"
                          style={{
                            background: "var(--primary-gradient)",
                            color: "white",
                            border: "none",
                          }}
                        >
                          View Live Demo
                        </Button>
                      )}
                      {project.repositoryUrl && (
                        <Button
                          leftSection={<IconBrandGithub size={16} />}
                          variant="outline"
                          component="a"
                          href={project.repositoryUrl}
                          target="_blank"
                          style={{
                            borderColor: "var(--purple-500)",
                            color: "var(--purple-400)",
                            backgroundColor: "rgba(139, 66, 255, 0.1)",
                          }}
                        >
                          View Source Code
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>

                {/* Tags */}
                {tags.length > 0 && (
                  <Paper
                    p="md"
                    style={{
                      backgroundColor: "var(--surface-background)",
                      border: "1px solid rgba(139, 66, 255, 0.2)",
                      borderRadius: "12px",
                    }}
                  >
                    <Stack gap="md">
                      <Title
                        order={4}
                        className="text-primary-dark"
                        fw={600}
                        size="sm"
                      >
                        Tags
                      </Title>
                      <Group gap="xs">
                        {tags.map((tag) => (
                          <Badge
                            key={tag.name}
                            variant="outline"
                            size="sm"
                            style={{
                              borderColor: "var(--purple-500)",
                              color: "var(--purple-400)",
                              backgroundColor: "rgba(139, 66, 255, 0.1)",
                            }}
                          >
                            {tag.name}
                          </Badge>
                        ))}
                      </Group>
                    </Stack>
                  </Paper>
                )}

                {/* Navigation Hints */}
                <Paper
                  p="md"
                  style={{
                    backgroundColor: "var(--surface-background)",
                    border: "1px solid rgba(139, 66, 255, 0.2)",
                    borderRadius: "12px",
                  }}
                >
                  <Stack gap="md">
                    <Title
                      order={4}
                      className="text-primary-dark"
                      fw={600}
                      size="sm"
                    >
                      Navigation
                    </Title>
                    <List
                      size="sm"
                      className="text-secondary-dark"
                      spacing="xs"
                    >
                      <List.Item>
                        Comprehensive project overview and technical details
                      </List.Item>
                      <List.Item>
                        Technology stack organized by category
                      </List.Item>
                      <List.Item>
                        Direct links to live demo and source code
                      </List.Item>
                    </List>
                  </Stack>
                </Paper>
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}
